import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { formatDate } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { RouterLink } from '@angular/router';
import { BreadcrumbComponent } from '../../../../shared/components/breadcrumb/breadcrumb.component';
import {
  JOB_STATUSES_NEW_TAGS,
  JOB_STATUSES_ACTIVE,
  LANGUAGES,
} from '../../../../shared/config/constants';
import {
  Estimate,
  VendorOrder,
} from '../../../../shared/models/vendor-order.model';
import { VendorOrdersService } from './services/vendor-orders.service';
import { NotificationService } from '../../../../shared/services/notification.service';

interface StatusItem {
  label: string;
  count: number;
}

interface TranslationJobRow {
  jobId: string;
  jobName: string;
  requestDate: string;
  requestTimestamp: number;
  sourceLanguage: string;
  targetLanguages: string[];
  targetOverflowCount: number;
  targetDate: string;
  vendor: string;
  statusLabel: string;
  statusKey: string;
  statusIcon: string | null;
}

const STATUS_LABELS = Array.from(
  new Set(Object.values(JOB_STATUSES_NEW_TAGS)),
);

const LOCALE_LOOKUP = new Map<string, string>(
  Object.entries(LANGUAGES),
);

const STATUS_ICON_MAP: Readonly<Record<string, string>> = {
  'Estimated Quotes Pending': 'schedule',
  'Final Quote Pending': 'schedule',
  'Final Quote to be Reviewed': 'request_quote',
  'In Progress': 'article_person',
  'Cancelled': 'cancel',
  'Completed': 'check_circle',
  'Rejected': 'do_not_disturb_on',
  'Not Supported': 'warning',
};

@Component({
  selector: 'app-translation-manager',
  standalone: true,
  imports: [BreadcrumbComponent, MatIconModule, RouterLink],
  templateUrl: './translation-manager.component.html',
  styleUrl: './translation-manager.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TranslationManagerComponent implements OnInit {
  private readonly vendorOrdersService = inject(VendorOrdersService);
  private readonly destroyRef = inject(DestroyRef);
  readonly notificationService = inject(NotificationService);
  private readonly vendorOrders = signal<VendorOrder[]>([]);
  private readonly cameFromDetail = history.state?.fromDetail === true;
  private readonly cameFromBreadcrumb = history.state?.fromBreadcrumb === true;
  private readonly incomingNotificationSnapshot =
    typeof history.state?.notificationSnapshot === 'string'
      ? history.state.notificationSnapshot
      : '';
  private readonly incomingFilters = this.getIncomingFilters();
  readonly sortDescending = signal(true);
  readonly activeFilters = signal<Set<string>>(new Set());

  readonly isLoading = signal(true);
  readonly errorMessage = signal<string | null>(null);

  private readonly selectedFiltersHistorySync = effect(() => {
    this.updateHistorySelectedFilters(this.activeFilterList());
  });

  readonly activeFilterList = computed(() => [...this.activeFilters()]);

  private readonly allRows = computed<TranslationJobRow[]>(() => {
    const direction = this.sortDescending() ? -1 : 1;

    return this.vendorOrders()
      .map((order) => this.mapOrderToRow(order))
      .sort(
        (first, second) =>
          (first.requestTimestamp - second.requestTimestamp) * direction,
      );
  });

  readonly rows = computed<TranslationJobRow[]>(() => {
    const all = this.allRows();
    const filters = this.activeFilters();

    if (filters.size === 0) {
      return all;
    }

    return all.filter((row) => filters.has(row.statusLabel));
  });

  readonly statusItems = computed<StatusItem[]>(() => {
    const allRows = this.allRows();

    return STATUS_LABELS.map((label) => ({
      label,
      count: allRows.filter((row) => row.statusLabel === label).length,
    }));
  });

  ngOnInit(): void {
    if (this.incomingFilters.length > 0) {
      this.activeFilters.set(new Set(this.incomingFilters));
    }

    this.loadOrders(this.shouldForceRefreshOnInit());
  }

  private shouldForceRefreshOnInit(): boolean {
    const cameFromReturnNavigation =
      this.cameFromDetail || this.cameFromBreadcrumb;

    if (!cameFromReturnNavigation) {
      return true;
    }

    if (!this.incomingNotificationSnapshot) {
      return false;
    }

    const snapshotOnEntry = this.parseNotificationSnapshot(
      this.incomingNotificationSnapshot,
    );
    const currentSnapshot = this.parseNotificationSnapshot(
      this.notificationService.notificationSnapshotKey(),
    );

    for (const key of currentSnapshot) {
      if (!snapshotOnEntry.has(key)) {
        return true;
      }
    }

    return false;
  }

  private parseNotificationSnapshot(snapshot: string): Set<string> {
    return new Set(
      snapshot
        .split('|')
        .map((item) => item.trim())
        .filter((item) => item.length > 0),
    );
  }

  toggleRequestDateSort(): void {
    this.sortDescending.update((value) => !value);
  }

  toggleFilter(label: string): void {
    this.activeFilters.update((current) => {
      if (current.has(label)) {
        return current;
      }
      const next = new Set(current);
      next.add(label);
      return next;
    });
  }

  removeFilter(label: string): void {
    this.activeFilters.update((current) => {
      const next = new Set(current);
      next.delete(label);
      return next;
    });
  }

  isFilterActive(label: string): boolean {
    return this.activeFilters().has(label);
  }

  hasStatusNotification(label: string): boolean {
    return this.notificationService.notifiedStatusLabels().has(label);
  }

  hasRowNotification(jobId: string): boolean {
    if (!jobId || jobId === '--') {
      return false;
    }

    return this.notificationService.isAwaitingVendorJob(jobId) || this.notificationService.isCompletedDocsJob(jobId);
  }

  private loadOrders(forceRefresh = false): void {
    this.isLoading.set(true);
    this.errorMessage.set(null);

    this.vendorOrdersService
      .getVendorOrders(false, forceRefresh)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (orders) => {
          this.vendorOrders.set(orders ?? []);
          this.isLoading.set(false);
        },
        error: () => {
          this.vendorOrders.set([]);
          this.isLoading.set(false);
          this.errorMessage.set('Unable to load translation jobs, please refresh the page or try again later.');
        },
      });
  }

  private mapOrderToRow(order: VendorOrder): TranslationJobRow {
    const targetLanguages = this.getTargetLanguages(order.targetLocaleId);
    const visibleTargets = targetLanguages.slice(0, 2);

    const statusLabel = this.getStatusLabel(order.jobStatus);

    return {
      jobId: order.jobUid ?? '--',
      jobName: order.customJobName ?? '--',
      requestDate: this.formatDateValue(order.createdAt),
      requestTimestamp: order.createdAt ?? 0,
      sourceLanguage: this.getLocaleName(order.sourceLocaleId),
      targetLanguages: visibleTargets,
      targetOverflowCount: Math.max(0, targetLanguages.length - 2),
      targetDate: this.formatDateValue(this.getTargetDate(order)),
      vendor: this.getVendorName(order.estimates),
      statusLabel,
      statusKey: this.getStatusKey(statusLabel),
      statusIcon: this.getStatusIcon(statusLabel),
    };
  }

  private getTargetDate(order: VendorOrder): string | undefined {
    const estimatedDate = order.estimatedDeliveryDate;
    const fixedDate = order.fixedDeliveryDate;
    let targetDate = order.deadline;

    if (estimatedDate) {
      targetDate = estimatedDate;
    }

    if (fixedDate && fixedDate !== estimatedDate) {
      targetDate = fixedDate;
    }

    return targetDate;
  }

  private formatDateValue(value?: number | string): string {
    if (value === undefined || value === null) {
      return '--';
    }

    const dateValue = typeof value === 'number' ? new Date(value) : new Date(value);
    if (Number.isNaN(dateValue.getTime())) {
      return '--';
    }

    return formatDate(dateValue, 'dd/MMM/yyyy', 'en-GB');
  }

  private getLocaleName(localeId?: string): string {
    if (!localeId) {
      return '--';
    }

    return LOCALE_LOOKUP.get(localeId) ?? localeId;
  }

  private getTargetLanguages(localeIds?: string[]): string[] {
    if (!localeIds || localeIds.length === 0) {
      return [];
    }

    return localeIds.map((localeId) => this.getLocaleName(localeId));
  }

  private getVendorName(estimates?: Estimate[]): string {
    if (!estimates || estimates.length === 0) {
      return '--';
    }

    const acceptedEstimate = estimates.find(
      (estimate) =>
        !!estimate.jobStatus 
        && JOB_STATUSES_ACTIVE.includes(estimate.jobStatus as typeof JOB_STATUSES_ACTIVE[number])
    );

    return acceptedEstimate?.name ?? '--';
  }

  private getStatusLabel(status?: string): string {
    if (!status) {
      return '--';
    }

    const mappedStatus = JOB_STATUSES_NEW_TAGS[status as keyof typeof JOB_STATUSES_NEW_TAGS];
    if (mappedStatus) {
      return mappedStatus;
    }

    return this.formatFallbackStatus(status);
  }

  private formatFallbackStatus(status: string): string {
    return status
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, (match) => match.toUpperCase());
  }

  private getStatusKey(statusLabel?: string): string {
    if (!statusLabel || statusLabel === '--') {
      return 'unknown';
    }

    return statusLabel
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '');
  }

  private getStatusIcon(statusLabel?: string): string | null {
    if (!statusLabel || statusLabel === '--') {
      return null;
    }

    return STATUS_ICON_MAP[statusLabel] ?? null;
  }

  private updateHistorySelectedFilters(filters: string[]): void {
    const nextState = { ...(history.state ?? {}) } as {
      selectedFilters?: string[];
    };

    if (filters.length > 0) {
      nextState.selectedFilters = [...filters];
    } else {
      delete nextState.selectedFilters;
    }

    history.replaceState(nextState, '');
  }

  private getIncomingFilters(): string[] {
    const state = history.state as { selectedFilters?: string[] } | null;
    if (!Array.isArray(state?.selectedFilters)) {
      return [];
    }

    return state.selectedFilters.filter((value) => typeof value === 'string' && value.trim().length > 0);
  }

}
